import React, { useEffect, useState, useRef } from 'react';
import { useStoreState } from 'easy-peasy';
import * as R from 'ramda';
import { Dropdown, Menu } from 'antd';
import { FormattedMessage } from 'react-intl';
import FilterDropdown from 'Components/filters/FilterDropdown';
import { FilterTypes, useFilters } from 'Components/filters/context';
import { useInvalidateMarketOrders } from './hooks/useMarketOrders';

export const useDivisionWarehouse = () => {
  const solution = useStoreState((state) => state.auth.solution);
  const isdao = useStoreState((state) => state.auth.isdao);
  const divisions = useStoreState((state) => state.auth.solutionDivisions);

  const invalidateMarketOrders = useInvalidateMarketOrders();
  const {
    filters,
    setFilters,
    warehouse: selWarehouse,
    setWarehouse,
  } = useFilters();

  const [warehouses, setWarehouses] = useState([]);

  // Get the current division from filters, not from market store
  const filterDivisionId = filters?.division?.items?.[0];
  const selDivision = React.useMemo(
    () => divisions.find((item) => item.id === filterDivisionId) || null,
    [divisions, filterDivisionId]
  );

  // Compute the warehouse that should be selected
  const targetWarehouse = React.useMemo(() => {
    if (!selDivision) return null;
    if (!selDivision.warehouses.length) return null;
    return (
      R.find(R.propEq('default', true))(selDivision.warehouses) ||
      selDivision.warehouses[0]
    );
  }, [selDivision]);

  // Update warehouses when division changes
  useEffect(() => {
    if (selDivision) {
      setWarehouses(selDivision.warehouses);
    } else {
      setWarehouses([]);
    }
  }, [selDivision]);

  // Update warehouse only when the ID actually changes
  useEffect(() => {
    const currentWarehouseId = selWarehouse?.id;
    const targetWarehouseId = targetWarehouse?.id;

    if (!targetWarehouse) return;

    if (currentWarehouseId !== targetWarehouseId) {
      setWarehouse(targetWarehouse);
    }
  }, [targetWarehouse, selWarehouse, setWarehouse]);

  // Invalidate only when warehouse ID actually changes
  const warehouseIdRef = useRef(selWarehouse?.id);
  useEffect(() => {
    const currentId = selWarehouse?.id;
    if (warehouseIdRef.current !== currentId) {
      warehouseIdRef.current = currentId;
      invalidateMarketOrders();
    }
  }, [selWarehouse?.id, invalidateMarketOrders]);

  const updateDivision = React.useCallback(
    (division) => {
      setFilters({
        ...filters,
        division: division
          ? { type: FilterTypes.RADIO, items: [division.id] }
          : { type: FilterTypes.RADIO, items: [] },
      });
    },
    [filters, setFilters]
  );

  const updateWarehouse = (warehouse) => {
    if (selWarehouse !== warehouse) {
      setWarehouse(warehouse);
    }
  };

  return {
    solution,
    isdao,
    selDivision,
    selWarehouse,
    divisions,
    warehouses,
    updateDivision,
    updateWarehouse,
  };
};

export default () => {
  const {
    solution,
    isdao,
    selDivision,
    selWarehouse,
    divisions,
    warehouses,
    updateDivision,
    updateWarehouse,
  } = useDivisionWarehouse();

  const divisionsMenu = (
    <Menu>
      <Menu.Item onClick={() => updateDivision(null)} key="default">
        <FormattedMessage
          id="marketplace-filter-divisions"
          description="Text for 'all divisions' option"
          defaultMessage="All divisions"
        />
      </Menu.Item>
      {divisions.map((item) => (
        <Menu.Item onClick={() => updateDivision(item)} key={item.id}>
          {item.name}
        </Menu.Item>
      ))}
    </Menu>
  );

  const warehousesMenu = (
    <Menu>
      {warehouses.map((item) => (
        <Menu.Item onClick={() => updateWarehouse(item)} key={item.id}>
          {item.desc}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <>
      {divisions.length > 1 && !isdao && (
        <Dropdown overlay={divisionsMenu} trigger={['click']}>
          <a className="show-dd-items" href="#division">
            {selDivision ? (
              selDivision.name
            ) : (
              <FormattedMessage
                id="marketplace-filter-divisions"
                description="Text for 'all divisions' option"
                defaultMessage="All divisions"
              />
            )}
          </a>
        </Dropdown>
      )}

      {solution.freight && warehouses.length > 1 && selWarehouse && (
        <Dropdown overlay={warehousesMenu} trigger={['click']}>
          <a className="show-dd-items" href="#warehouse">
            {selWarehouse?.desc}
          </a>
        </Dropdown>
      )}
    </>
  );
};

export const DivisionWarehouseFilter = () => {
  const { isdao, divisions, updateDivision } = useDivisionWarehouse();

  const divisionOptions = divisions.map((item) => ({
    value: item.id,
    label: item.name,
  }));
  return (
    <>
      {divisions.length > 1 && !isdao && (
        <FilterDropdown
          id="division"
          label={
            <FormattedMessage
              id="marketplace-filter-divisions"
              description="Text for 'all divisions' option"
              defaultMessage="All divisions"
            />
          }
          type={FilterTypes.RADIO}
          options={divisionOptions}
          onClear={() => updateDivision(null)}
        />
      )}
    </>
  );
};
